import { useState, useRef, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import User1 from 'assets/images/4811117-small.png';

// assets
import { IconLogout, IconSettings } from '@tabler/icons';
import { logOut } from 'firebaseService/firebaseAuth';
import SnackbarAlert from 'ui-component/Alerts/SnackbarAlerts';
import { getGreeting } from 'utils/DateTime';
import ProfileModal from './ProfileModal';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();
  const customization = useSelector((state) => state.customization);
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  // const [selectedIndex, setSelectedIndex] = useState(-1);
  const [open, setOpen] = useState(false);

  const [snackbarData, setSnackbarData] = useState({
    open: false,
    severity: 'success',
    message: '',
    duration: 3000
  });

  const closeSnackbar = () => {
    setSnackbarData({
      ...snackbarData,
      open: false
    });
  };

  const onClose = () => {
    setIsOpen(false);
  };

  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);
  const handleLogout = async () => {
    try {
      const result = await logOut();

      const successMessage = 'Cierre de sesión exitoso';
      const errorMessage = 'Error al cerrar sesión:';

      if (result.error) {
        console.error(errorMessage, result.error);
        setSnackbarData({
          open: true,
          severity: 'error',
          message: `${errorMessage} ${result.error.message}`
        });
      } else {
        setSnackbarData({
          open: true,
          severity: 'success',
          message: successMessage
        });
      }
    } catch (error) {
      console.error(error);

      setSnackbarData({
        open: true,
        severity: 'error',
        message: 'Error inesperado'
      });
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleListItemClick = (event, index, route = '') => {
    handleClose(event);

    if (route && route !== '') {
      navigate(route);
    }
  };
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const getRole = (role) => {
    let roleAsString = '';
    if (role === 'USER_ROLE') {
      roleAsString = 'Normal';
    } else if (role === 'ADMIN_ROLE') {
      roleAsString = 'Administrador';
    } else if (role === 'PM_ROLE') {
      roleAsString = 'Projec Manager';
    } else {
      roleAsString = 'Normal';
    }

    return roleAsString;
  };

  const handlerProfile = () => {
    setIsOpen(true);
  };

  return (
    <>
      <ProfileModal open={isOpen} onClose={onClose} user={user} />
      <Chip
        sx={{
          height: '48px',
          alignItems: 'center',
          borderRadius: '27px',
          transition: 'all .2s ease-in-out',
          borderColor: theme.palette.primary.light,
          backgroundColor: theme.palette.primary.light,
          '&[aria-controls="menu-list-grow"], &:hover': {
            borderColor: theme.palette.primary.main,
            background: `${theme.palette.primary.main}!important`,
            color: theme.palette.primary.light,
            '& svg': {
              stroke: theme.palette.primary.light
            }
          },
          '& .MuiChip-label': {
            lineHeight: 0
          }
        }}
        icon={
          <Avatar
            src={User1}
            sx={{
              ...theme.typography.mediumAvatar,
              margin: '8px 0 8px 8px !important',
              cursor: 'pointer'
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            color="inherit"
          />
        }
        label={<IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />}
        variant="outlined"
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
      />
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                  <Box sx={{ p: 2 }}>
                    <Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4">{getGreeting()}</Typography>
                        <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                          {user.displayName}
                        </Typography>
                      </Stack>
                      <Typography variant="subtitle2">{getRole(JSON.parse(user?.reloadUserInfo?.customAttributes).role)}</Typography>
                    </Stack>
                    <Divider />
                  </Box>
                  <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                    <Box>
                      <List
                        component="nav"
                        sx={{
                          width: '100%',
                          maxWidth: 350,
                          minWidth: 300,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: '10px',
                          [theme.breakpoints.down('md')]: {
                            minWidth: '100%'
                          },
                          '& .MuiListItemButton-root': {
                            mt: 0.5
                          }
                        }}
                      >
                        <ListItemButton
                          sx={{ borderRadius: `${customization.borderRadius}px` }}
                          onClick={(event) => handleListItemClick(event, 0, '#')}
                        >
                          <ListItemIcon>
                            <IconSettings stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText onClick={handlerProfile} primary={<Typography variant="body2">Configurar Cuenta</Typography>} />
                        </ListItemButton>
                        <ListItemButton sx={{ borderRadius: `${customization.borderRadius}px` }} onClick={handleLogout}>
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">Cerrar Sesion</Typography>} />
                        </ListItemButton>
                      </List>
                    </Box>
                  </PerfectScrollbar>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
      <SnackbarAlert {...snackbarData} closeSnackbar={closeSnackbar} />
    </>
  );
};

export default ProfileSection;
